/**
 * "Answers for you · N unread" (proposal 1, kanban 059dd612): one row per
 * answer nobody has marked seen, saying who answered which question, when it
 * was asked and answered, how it came in, and which case or item it belongs
 * to. Read opens the answer's drawer over the page; Mark seen clears only
 * that answer. An answer with no case or item can be attached to an open
 * case from the row.
 */
import type { DoorInvestigationRead } from '../../services/qcpInvestigations';
import { trackEvent } from '../../services/portalTracking';
import {
  answeredWhen,
  askedLine,
  channelPhrase,
  type AnswerRow,
} from '../../pages/quality-triage/answersView';
import { AnswerPlace } from './AnswerPlace';
import styles from './AnswersForYou.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface AnswersForYouProps {
  rows: AnswerRow[];
  investigations: DoorInvestigationRead[];
  userName: string | null;
  onRead: (requestId: string) => void;
  onMarkSeen: (requestId: string) => void;
  onAttach: (requestId: string, investigationId: string) => void;
  /** A request id whose Mark seen / Attach is in flight (its buttons wait). */
  busyId?: string | null;
}

export function AnswersForYou({
  rows,
  investigations,
  userName,
  onRead,
  onMarkSeen,
  onAttach,
  busyId = null,
}: AnswersForYouProps) {
  // A read-only viewer reads the answers; the desk marks and files them (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  if (rows.length === 0) return null;
  return (
    <section className={styles.box} aria-labelledby="answers-for-you" data-testid="answers-for-you">
      <h2 id="answers-for-you" className={styles.head}>
        Answers for you <span className={styles.unread}>{rows.length} unread</span>
      </h2>
      <ul className={styles.list}>
        {rows.map((row) => {
          const busy = busyId === row.request.id;
          const ids = {
            data_request_id: row.request.id,
            investigation_no: row.investigation?.investigation_no ?? null,
            thread_no: row.thread?.thread_no ?? null,
          };
          return (
            <li key={row.request.id} className={styles.row} data-testid="answer-row">
              <span className={styles.pill}>unread</span>
              <div className={styles.text}>
                <div className={styles.line}>
                  <strong>{row.who}</strong> answered <em>“{row.question}”</em>
                </div>
                <div className={styles.meta}>
                  {askedLine(row, userName)} · answered {answeredWhen(row.at)}
                  {channelPhrase(row.channel) && ` ${channelPhrase(row.channel)}`} ·{' '}
                  {!readOnly && <AnswerPlace row={row} investigations={investigations} busy={busy} onAttach={onAttach} />}
                </div>
              </div>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.read}
                  data-answer-read
                  onClick={() => {
                    trackEvent('action', 'answers_read', { ...ids, result: 'panel_opened' });
                    onRead(row.request.id);
                  }}
                >
                  Read
                </button>
                {!readOnly && (<button
                  type="button"
                  className={styles.seen}
                  disabled={busy}
                  onClick={() => {
                    trackEvent('action', 'answers_mark_seen', { ...ids, result: 'request_sent' });
                    onMarkSeen(row.request.id);
                  }}
                >
                  Mark seen
                </button>)}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default AnswersForYou;
